import { useQuery } from '@apollo/client';
import { navigate } from 'gatsby';
import React, { useContext, useEffect, useState } from 'react';
import { useSearchParam } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import { DashboardCardLayout } from '@ui/atoms/DashboardCardLayout';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';
import TextLink, { ITextLinkProps } from '@ui/atoms/TextLink';

import { MembershipOptionCard } from '@ui/molecules/MembershipOptionCard';

import MembershipCard from '@ui/organisms/MembershipCard';
import ReactivateMembershipHandler from '@ui/organisms/ReactivateMembershipHandler';
import { Loading } from '@ui/organisms/ReactivateMembershipHandler/ReactivateMembershipHandler.component';

import aligned from '@ui/styled/Aligned';
import padded from '@ui/styled/Padded';
import spaced from '@ui/styled/Spaced';

import { IAuthenticatedMember, SubscriptionInfo } from '@hooks/useMemberInfo';

import { commonLabels } from '@utils/button';
import { doesNotExist } from '@utils/general';
import { combineMemberFragments } from '@utils/graphql';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';
import {
	IFolxMembershipBenefitsCard,
	IFolxMembershipOption,
} from '@folx-types/shared-folx-membership-information';

import { membership, membershipName, queryName } from '@graphql/fragments/membership';

import theme from '@theme/FolxTheme';

export interface IReactivateMembershipSummaryPageContent {
	heading: string;
	chargeDateText: string;
	chargeFrequencyText: string;
	disclaimer: string;
	folxMembershipBenefitsCard: IFolxMembershipBenefitsCard;
	folxMembershipOptionMonthly: IFolxMembershipOption;
	folxMembershipOptionYearly: IFolxMembershipOption;
}

interface IReactivateMembershipSummaryProps extends IPageProps {
	pageContent: IReactivateMembershipSummaryPageContent;
	loading: boolean;
	paymentInfo: string;
	price: { annual: number; monthly: number };
}

const ReactivateMembershipSummaryCardLayout = styled(DashboardCardLayout)`
	max-width: 100%;
	overflow-x: hidden;
	padding: 0 ${theme.spacing.md};
	@media ${theme.responsive.breakpoints.laptop} {
		overflow-x: visible;
		padding: 0;
	}
`;

const AlignedLink = aligned<ITextLinkProps>(TextLink);
const SpacedColumn = spaced<IFlexColumn>(FlexColumn);
const PaddedColumn = padded<IFlexColumn>(FlexColumn);
const StyledButtons = styled(SpacedColumn)`
	flex-flow: row nowrap;
`;

const StyledButton = styled(Button)`
	flex-grow: 1;
	width: unset;
`;

const ReactivateMembershipSummary = ({
	pageContent,
	loading,
	paymentInfo,
	price,
}: IReactivateMembershipSummaryProps) => {
	const {
		heading,
		chargeDateText,
		chargeFrequencyText,
		disclaimer,
		folxMembershipBenefitsCard,
		folxMembershipOptionMonthly,
		folxMembershipOptionYearly,
	} = pageContent;

	const [isProcessingPayment, setIsProcessingPayment] = useState(false);
	const [isPendingCancellation, setIsPendingCancellation] = useState(false);

	const [selectedMembershipOption, setSelectedMembershipOption] = useState<'MONTH' | 'YEAR'>(
		'MONTH'
	);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const {
		data: membershipData,
		loading: membershipLoading,
		error: membershipError,
	} = useQuery<IAuthenticatedMember<SubscriptionInfo>>(
		combineMemberFragments([membershipName], [membership], queryName),
		{
			client: graphQLv2Client,
		}
	);

	useEffect(() => {
		if (doesNotExist(membershipData)) return;
		const membershipSubscription = membershipData?.authenticatedMember.membershipSubscription;
		if (membershipSubscription?.cancelAt && membershipSubscription?.status !== 'CANCELED') {
			setIsPendingCancellation(true);
		}
	}, [membershipLoading]);

	// user should come back to where it came from, or go to starting page
	const backToUrlMapper = (backTo: string | null) => {
		switch (backTo) {
			case 'billing':
				return '../../billing';
			case 'get_care':
				return '../../get_care';
			default:
				return '../../';
		}
	};

	const backTo = useSearchParam('backTo');
	const navigateUrl = backToUrlMapper(backTo);

	return (
		<ReactivateMembershipSummaryCardLayout>
			<LoadingWrapper loading={membershipLoading} skeleton={<Loading />}>
				<HeadingText>{heading}</HeadingText>

				{isProcessingPayment || isPendingCancellation ? (
					<ReactivateMembershipHandler
						isPendingCancellation={isPendingCancellation ? Boolean(isPendingCancellation) : false}
						currentBillingInterval={
							membershipData?.authenticatedMember.membershipSubscription.billingInterval
						}
						selectedBillingInterval={selectedMembershipOption}
					/>
				) : (
					<>
						<LoadingWrapper loading={loading} skeleton={<Skeleton height="xxxxxl" fullWidth />}>
							<SpacedColumn gap="md">
								<MembershipCard
									header={folxMembershipBenefitsCard.title}
									topics={folxMembershipBenefitsCard.membershipTopics}
								/>

								<FlexColumn gap="sm">
									<MembershipOptionCard
										content={folxMembershipOptionMonthly}
										isSelected={selectedMembershipOption === 'MONTH'}
										onClick={() => setSelectedMembershipOption('MONTH')}
										price={price.monthly}
									/>

									<MembershipOptionCard
										content={folxMembershipOptionYearly}
										isSelected={selectedMembershipOption === 'YEAR'}
										onClick={() => setSelectedMembershipOption('YEAR')}
										price={price.annual}
									/>
								</FlexColumn>

								<PaddedColumn padding="md" paddingTop="0" paddingBottom="0" gap="xs">
									<BasicText variant="fineprint">{chargeDateText}</BasicText>
									<BasicText variant="fineprint">{paymentInfo}</BasicText>
									<BasicText variant="fineprint">{disclaimer}</BasicText>
								</PaddedColumn>
							</SpacedColumn>
						</LoadingWrapper>

						<StyledButtons gap="sm">
							<StyledButton
								label={commonLabels.back}
								onClick={() => navigate(navigateUrl)}
								labelSize="sm"
							/>
							<StyledButton
								label={commonLabels.continue}
								onClick={() => setIsProcessingPayment(true)}
								disabled={loading}
								variant="secondary"
								labelSize="sm"
							/>
						</StyledButtons>
						<AlignedLink
							alignSelf="center"
							label="Update my card on file"
							href="../../billing/payment_edit"
						/>
					</>
				)}
			</LoadingWrapper>
		</ReactivateMembershipSummaryCardLayout>
	);
};

export default ReactivateMembershipSummary;
